import { useCallback, useEffect, useMemo, useState } from "react";

import {
  faChevronRight,
  faSearch,
  faSparkles,
} from "@fortawesome/pro-regular-svg-icons";
import { twJoin } from "tailwind-merge";

import { MarkdownRenderer } from "src/base-components/MarkdownRenderer/MarkdownRenderer";
import { Pill } from "src/base-components/Pill";
import { SkeletonPlaceholder } from "src/base-components/SkeletonPlaceholder";
import { CaseAction } from "src/cases/caseView/CaseAction";
import { TaskCheckbox } from "src/cases/caseView/components/TaskCheckbox";
import { TaskFormSidebarView } from "src/cases/caseView/components/TaskFormSidebarView";
import {
  useAvailableCaseTasks,
  useGetCaseTemplateTask,
  useTaskFormValidForCompletion,
} from "src/cases/caseView/hooks";
import { isCaseCopilotEnabled } from "src/cases/copilot/isCaseCopilotEnabled";
import {
  CaseDeserialized,
  type CaseTask,
  transformCaseTask,
} from "src/cases/queries/casesTypes";
import { useTasksWithHints } from "src/cases/queries/hints";
import { CaseTemplateTaskOutput } from "src/clients/case-management";
import { EmptyState } from "src/design-system/EmptyState";
import { Icon } from "src/design-system/Icon";
import { useCapabilities } from "src/hooks/useCapabilities";
import { useCurrentUserId } from "src/store/AuthStore";

type TaskItemProps = {
  task: CaseTask;
  templateTask?: CaseTemplateTaskOutput;
  caseData?: CaseDeserialized;
  index: number;
  totalTasks: number;
  isDisabled?: boolean;
  hasHints: boolean;
  onOpenSidebarForm: () => void;
};

const TaskItem: React.FC<TaskItemProps> = ({
  task,
  templateTask,
  caseData,
  index,
  totalTasks,
  isDisabled = false,
  hasHints = false,
  onOpenSidebarForm,
}) => {
  const hasForm = !!templateTask?.form?.fields?.length;

  const canCompleteTask = useTaskFormValidForCompletion(
    templateTask,
    task.formValues,
  );

  const handleOpenForm = useCallback(() => {
    if (!hasForm) return;
    onOpenSidebarForm();
  }, [hasForm, onOpenSidebarForm]);

  const containerClasses = twJoin(
    "flex flex-col gap-2 border border-gray-200 p-3 pl-2",
    index === 0 ? "rounded-t-lg" : "border-t-0",
    index === totalTasks - 1 && "rounded-b-lg",
    hasForm && "cursor-pointer",
  );

  const isCheckboxDisabled =
    isDisabled ||
    hasHints ||
    (hasForm && !task.isCompleted && !canCompleteTask);

  return (
    <div
      className={containerClasses}
      data-loc={`task-item-${task.key}`}
      onClick={hasForm ? handleOpenForm : undefined}
    >
      <div className="flex w-full items-start gap-2">
        <label
          className={twJoin(
            "flex items-center p-1",
            isCheckboxDisabled ? "cursor-not-allowed" : "cursor-pointer",
          )}
          onClick={hasForm ? (e) => e.stopPropagation() : undefined}
        >
          <TaskCheckbox
            caseData={caseData}
            disabled={isCheckboxDisabled}
            task={task}
          />
        </label>

        <div className="flex min-w-0 flex-1 flex-col gap-1">
          <div className="flex items-center gap-2">
            <span
              className="flex-1 font-inter-semibold-13px text-gray-800"
              data-loc={`task-title-${task.key}`}
            >
              {task.title}
            </span>

            {hasHints && (
              <Pill size="sm" variant="indigo-light">
                <Pill.Icon icon={faSparkles} />
                <Pill.Text>Filled by Copilot</Pill.Text>
              </Pill>
            )}

            {hasForm && (
              <div className="flex shrink-0 items-center">
                <Icon icon={faChevronRight} size="xs" />
              </div>
            )}
          </div>

          {task.description && (
            <MarkdownRenderer
              content={task.description}
              dataLoc={`task-description-${task.key}`}
              variant="muted"
            />
          )}
        </div>
      </div>
    </div>
  );
};

export const CaseTasks: React.FC<{
  caseData?: CaseDeserialized;
  isLoading?: boolean;
}> = ({ caseData, isLoading }) => {
  const availableTasks = useAvailableCaseTasks(caseData);
  const getTemplateTask = useGetCaseTemplateTask(caseData);
  const capabilities = useCapabilities();
  const currentUserId = useCurrentUserId();
  const [selectedTaskKey, setSelectedTaskKey] = useState<string | null>(null);
  const copilotEnabled = isCaseCopilotEnabled(caseData);
  const taskKeysWithHints = useTasksWithHints(caseData?.data.id, {
    enabled: copilotEnabled,
  });

  // Check if user can edit: admin or assigned to the case
  const canEdit = (() => {
    // Admins can always edit
    if (capabilities.cases.canManage) {
      return true;
    }
    // Queue members can edit if they're assigned to the case
    if (!currentUserId) {
      return false;
    }
    const assigneeId = caseData?.data.assignee?.id;
    return assigneeId === currentUserId;
  })();

  const tasks = useMemo(
    () =>
      availableTasks.map((caseTask) =>
        transformCaseTask(caseTask, getTemplateTask),
      ),
    [availableTasks, getTemplateTask],
  );

  // Reset selected task if it becomes invalid (e.g. task disappears)
  useEffect(() => {
    if (selectedTaskKey && !tasks.some((t) => t.key === selectedTaskKey)) {
      setSelectedTaskKey(null);
    }
  }, [tasks, selectedTaskKey]);

  if (isLoading) {
    return <LoadingTasks />;
  }

  const selectedTask = tasks.find((t) => t.key === selectedTaskKey);
  const selectedTemplateTask = selectedTask
    ? getTemplateTask(selectedTask.key)
    : undefined;

  if (selectedTask && selectedTemplateTask) {
    return (
      <TaskFormSidebarView
        caseData={caseData}
        disabled={!canEdit}
        task={selectedTask}
        templateTask={selectedTemplateTask}
        onBack={() => setSelectedTaskKey(null)}
      />
    );
  }

  const noTasks = tasks.length === 0;

  return (
    <div
      className="flex min-h-full flex-col p-4 pb-0"
      data-loc={`case-tasks${noTasks ? "-empty" : ""}`}
    >
      <div
        className="decideScrollbar flex min-h-0 flex-1 flex-col"
        data-loc="task-list"
      >
        {noTasks ? (
          <EmptyState
            description="There are no tasks to complete for this status"
            headline="No tasks to complete"
            icon={faSearch}
          />
        ) : (
          tasks.map((task, index) => {
            const templateTask = getTemplateTask(task.key);

            return (
              <TaskItem
                key={task.id}
                caseData={caseData}
                hasHints={taskKeysWithHints.has(task.key)}
                index={index}
                isDisabled={!canEdit}
                task={task}
                templateTask={templateTask}
                totalTasks={tasks.length}
                onOpenSidebarForm={() => setSelectedTaskKey(task.key)}
              />
            );
          })
        )}
      </div>

      <ReviewerInputSection caseData={caseData} isDisabled={!canEdit} />
    </div>
  );
};

const ReviewerInputSection: React.FC<{
  caseData?: CaseDeserialized;
  isDisabled?: boolean;
}> = ({ caseData, isDisabled = false }) => (
  <div className="flex flex-col gap-y-3 border-gray-200 border-t bg-white pb-4">
    <div className="flex border-gray-200 border-b">
      <div className="relative flex px-1 py-3 after:absolute after:bottom-0 after:left-0 after:h-[3px] after:w-full after:rounded-full after:bg-indigo-600">
        <span className="font-inter-semibold-13px">Reviewer Input</span>
      </div>
    </div>
    <CaseAction caseData={caseData} isDisabled={isDisabled} />
  </div>
);

const LoadingTasks: React.FC = () => (
  <div className="flex min-h-full flex-col p-4" data-loc="case-tasks-loading">
    <div className="flex flex-col">
      {[...Array(3)].map((_, i) => (
        <div
          key={i}
          className={`border border-gray-200 p-3 ${
            i === 0 ? "rounded-t-lg" : "border-t-0"
          } ${i === 2 ? "rounded-b-lg" : ""}`}
        >
          <div className="flex items-start gap-2">
            <SkeletonPlaceholder height="h-4" width="w-4" />
            <div className="flex-1 space-y-1">
              <SkeletonPlaceholder height="h-4" width="w-32" />
              <SkeletonPlaceholder height="h-3" width="w-48" />
            </div>
          </div>
        </div>
      ))}
    </div>
  </div>
);
